import { highLevelApi } from '@sdk/main'
import type { PitcherEnv } from '@sdk/interfaces'
import type {
  AICompletePayload,
  AICompleteResult,
  AIRuntimeCapabilities,
  PiaSearchAnswerPayload,
  PiaSearchAnswerResult,
} from '@sdk/payload.types'
import { resolvePiaSearchMode } from '@lib/types/launchDarkly.types'

/**
 * AI surface of the JS API (PIT-6863): Impact owns AI inference on every
 * platform behind ONE surface.
 *
 * - iOS: `ai.get_capabilities` / `ai.complete` are answered by the native
 *   on-device (Gemma) bridge over the standard PITCHER_REQUEST postMessage.
 * - Web: there is no local model — capabilities resolve statically to the
 *   online (Bedrock) runtime and `piaSearchAnswer` calls the next-core
 *   `pia-prepare/pia-search` route directly with the env's access token.
 *
 * Casing note: `ai.complete`'s payload keys (`prompt`/`max_tokens`/`stream`)
 * are already snake_case, so LowLevelApi's `snakeCaseKeys` transform is a
 * no-op — no RAW_PAYLOAD_METHODS entry is needed (verified against the iOS
 * wire contract).
 */

const PIA_SEARCH_ROUTE = '/core/api/protected/pia-prepare/pia-search'
/** `ai.get_capabilities` bound so an older iOS build that never answers the
 * postMessage can't hang `piaSearchAnswer` forever (LowLevelApi has no
 * timeouts) — on timeout the caller falls through to the online path. */
const CAPABILITIES_TIMEOUT_MS = 3_000
const ON_DEVICE_MAX_TOKENS = 512
/** Online answer request bound so a hung/slow backend can't leave
 * `piaSearchAnswer` pending forever (the on-device path is already bounded by
 * `CAPABILITIES_TIMEOUT_MS`). This is the terminal path — on timeout the call
 * rejects and callers (e.g. the Hub) degrade to lexical search. */
const ONLINE_TIMEOUT_MS = 30_000

const WEB_CAPABILITIES: AIRuntimeCapabilities = {
  available: true,
  offline: false,
  runtime: 'bedrock-online',
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function normalizeOrigin(domain: string): string {
  return domain.startsWith('http') ? domain : `https://${domain}`
}

/** Customer API origin, from the token claims (canonical) with the org
 * metadata custom_domain as fallback — the slotty `pia.ui.ts` pattern. */
function apiOriginFromEnv(env: PitcherEnv): string {
  const claimDomain = env?.pitcher?.token_claims?.['https://pitcher.com/claims/urls']?.custom_domain
  if (typeof claimDomain === 'string' && claimDomain.length > 0) return normalizeOrigin(claimDomain)
  const orgDomain = env?.pitcher?.organization?.metadata?.custom_domain
  if (typeof orgDomain === 'string' && orgDomain.length > 0) return normalizeOrigin(orgDomain)
  throw new Error('piaSearchAnswer: no customer domain found in env (token_claims / organization metadata)')
}

function buildOnDevicePrompt(query: string, contextText?: string): string {
  const context =
    typeof contextText === 'string' && contextText.trim().length > 0
      ? contextText.trim()
      : 'No content context was provided.'
  return `You are PIA, a sales content assistant. Answer the rep's question using ONLY the CONTEXT below — do not invent content that is not in it.

CONTEXT:
${context}

QUESTION: ${query}

Answer concisely in plain text. If the context does not contain the answer, say you could not find it in the available content.`
}

/**
 * Capabilities of the AI completion runtime on the current platform.
 *
 * On iOS this asks the native on-device bridge (`ai.get_capabilities`); on web
 * it resolves statically to the online (Bedrock) runtime — no postMessage
 * round-trip.
 *
 * @returns {Promise<AIRuntimeCapabilities>} - Promise resolving with the runtime capabilities.
 *
 * @example
 * const capabilities = await api.aiGetCapabilities()
 * if (capabilities.available) { ... }
 */
export async function aiGetCapabilities(): Promise<AIRuntimeCapabilities> {
  const env: PitcherEnv = await highLevelApi.API.request('get_env')
  if (env?.mode === 'IOS') {
    return highLevelApi.API.request('ai.get_capabilities')
  }
  return { ...WEB_CAPABILITIES }
}

/**
 * Run an AI completion on the on-device model (iOS only). On web the host
 * rejects with a clear error — use `piaSearchAnswer` (or the next-core AI
 * routes) for online inference instead.
 *
 * The payload is the EXACT iOS wire contract: `{ prompt, max_tokens?, stream? }`
 * → `{ text, tokens_generated, elapsed_seconds, time_to_first_token_seconds }`.
 *
 * @param {AICompletePayload} payload - The completion payload.
 * @returns {Promise<AICompleteResult>} - Promise resolving with the completion.
 *
 * @example
 * const completion = await api.aiComplete({ prompt: 'Summarize ...', max_tokens: 256 })
 * console.log(completion.text)
 */
export function aiComplete(payload: AICompletePayload): Promise<AICompleteResult> {
  if (!payload || typeof payload.prompt !== 'string' || payload.prompt.trim() === '') {
    return Promise.reject(new Error('prompt is required and must be a non-empty string'))
  }
  return highLevelApi.API.request('ai.complete', payload)
}

/**
 * Answer a rep's natural-language question over the instance's content — the
 * ONE high-level PIA Search call (PIT-6863). Routing follows the
 * `pia_search_config` flag mode (see `resolvePiaSearchMode`):
 *
 * - `on_device` on iOS with an available local model → `aiComplete` over the
 *   caller-assembled `context_text` (the Hub has the file metadata
 *   client-side). Unavailable/downloading models fall through to online.
 * - `online` (or any on-device fallback) → POST to the next-core
 *   `pia-prepare/pia-search` Bedrock route, which assembles its own candidate
 *   context server-side.
 * - `off` → rejects.
 *
 * @param {PiaSearchAnswerPayload} payload - The question + optional context/narrowing.
 * @returns {Promise<PiaSearchAnswerResult>} - Unified answer with the serving `source`.
 *
 * @example
 * const result = await api.piaSearchAnswer({ query: 'What is our pricing for oncology?' })
 * console.log(result.answer, result.cited_file_ids, result.source)
 */
export async function piaSearchAnswer(payload: PiaSearchAnswerPayload): Promise<PiaSearchAnswerResult> {
  if (!payload || typeof payload.query !== 'string' || payload.query.trim() === '') {
    return Promise.reject(new Error('query is required and must be a non-empty string'))
  }

  const env: PitcherEnv = await highLevelApi.API.request('get_env')
  const mode = resolvePiaSearchMode(env?.launch_darkly?.pia_search_config)
  if (mode === 'off') {
    throw new Error('pia_search_disabled: pia_search_config resolves to mode "off"')
  }

  // On-device path — iOS only (web never has a local model). Any failure
  // (bridge timeout, model unavailable, completion error) falls through to the
  // online route.
  if (mode === 'on_device' && env?.mode === 'IOS') {
    try {
      const capabilities = await withTimeout(
        highLevelApi.API.request('ai.get_capabilities') as Promise<AIRuntimeCapabilities>,
        CAPABILITIES_TIMEOUT_MS,
        'ai.get_capabilities',
      )
      if (capabilities?.available) {
        const completion = await aiComplete({
          prompt: buildOnDevicePrompt(payload.query, payload.context_text),
          max_tokens: payload.max_tokens ?? ON_DEVICE_MAX_TOKENS,
        })
        return { answer: completion.text, source: 'on_device' }
      }
    } catch (error) {
      console.warn('[piaSearchAnswer] on-device path unavailable, falling back to online:', error)
    }
  }

  const instanceId = payload.instance_id || env?.pitcher?.instance?.id
  if (!instanceId) {
    throw new Error('piaSearchAnswer: instance_id could not be resolved from the payload or env')
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ONLINE_TIMEOUT_MS)
  try {
    const response = await fetch(`${apiOriginFromEnv(env)}${PIA_SEARCH_ROUTE}`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${env.pitcher.access_token}`,
      },
      body: JSON.stringify({
        query: payload.query,
        instance_id: instanceId,
        ...(Array.isArray(payload.file_ids) && payload.file_ids.length > 0 && { file_ids: payload.file_ids }),
      }),
    })
    if (!response.ok) {
      throw new Error(`pia_search ${response.status}`)
    }
    // Consume the body INSIDE the timed window: fetch() resolves on headers
    // alone, so a response that stalls mid-body would otherwise hang unbounded.
    // The abort signal cancels the body read too, so clearTimeout must wait
    // until json() settles (finally).
    const data = (await response.json()) as {
      pia_search?: { answer?: string; cited_file_ids?: string[]; confidence?: 'high' | 'medium' | 'low' }
    }
    const output = data?.pia_search ?? {}
    return {
      answer: typeof output.answer === 'string' ? output.answer : '',
      cited_file_ids: Array.isArray(output.cited_file_ids) ? output.cited_file_ids : [],
      ...(output.confidence && { confidence: output.confidence }),
      source: 'online',
    }
  } catch (error) {
    // AbortError name check is the cross-runtime signal (WKWebView included).
    if ((error as { name?: string })?.name === 'AbortError') {
      throw new Error(`pia_search online request timed out after ${ONLINE_TIMEOUT_MS}ms`)
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}
